import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { tasksApi, authApi } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { SearchableMultiSelect } from "@/components/SearchableMultiSelect";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";

export interface ChangeTaskAssigneeModalProps {
    isOpen: boolean;
    task: any | null;
    onClose: () => void;
    onSuccess: (updated: any) => void;
}

export const ChangeTaskAssigneeModal: React.FC<
    ChangeTaskAssigneeModalProps
> = ({ isOpen, task, onClose, onSuccess }) => {
    const { t } = useTranslation("global");
    const { user } = useAuth();
    const { showSuccess, showError } = useToast();
    const [selectedAssignee, setSelectedAssignee] = useState<number[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            authApi
                .users()
                .then((res: any) => {
                    const userData = Array.isArray(res?.data)
                        ? res.data
                        : Array.isArray(res?.data?.data)
                          ? res.data.data
                          : Array.isArray(res?.data?.users)
                            ? res.data.users
                            : [];

                    if (res?.result !== false) {
                        setUsers(userData);
                    }
                })
                .catch((err: any) =>
                    console.error("Error loading users for assignees", err)
                );
        }
    }, [isOpen]);

    useEffect(() => {
        if (task && task.assignee_id) {
            setSelectedAssignee([task.assignee_id]);
        } else {
            setSelectedAssignee([]);
        }

        setError(null);
    }, [task]);

    const assigneeOptions = useMemo(() => {
        return users.map((u: any) => ({
            id: u.id,
            name: u.name,
            subtitle: u.email,
        }));
    }, [users]);

    const isAssignedToMe = Boolean(
        user?.id && selectedAssignee.includes(user.id)
    );

    const handleAssignToMe = async (): Promise<void> => {
        if (!task || !user?.id) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await tasksApi.update(task.id, {
                assignee_id: user.id,
            });

            if (res.result) {
                showSuccess(t("tasks.modal_assignee.success"));
                const updatedRow = {
                    ...task,
                    assignee_id: user.id,
                    assignee: users.find((u: any) => u.id === user.id) || user,
                };
                onSuccess(updatedRow);
                onClose();
            } else {
                setError(res.message || t("errors.default"));
            }
        } catch (err) {
            console.error(err);
            setError(t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (): Promise<void> => {
        if (!task) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const assigneeId =
                selectedAssignee.length > 0 ? selectedAssignee[0] : null;
            const res = await tasksApi.update(task.id, {
                assignee_id: assigneeId,
            });

            if (res.result) {
                showSuccess(t("tasks.modal_assignee.success"));
                const updatedRow = {
                    ...task,
                    assignee_id: assigneeId,
                    assignee: assigneeId
                        ? users.find((u: any) => u.id === assigneeId)
                        : null,
                };
                onSuccess(updatedRow);
                onClose();
            } else {
                setError(res.message || t("errors.default"));
            }
        } catch (err) {
            console.error(err);
            setError(t("errors.default"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tasks.modal_assignee.title")}
            maxWidth="md"
            zIndex={100001}
            onSubmit={handleSubmit}
            isSubmitting={loading}
            submitLabel={t("tasks.modal_assignee.save_button")}
            submittingLabel={t("tasks.modal_assignee.saving")}
            hasUnsavedChanges={
                (selectedAssignee[0] ?? null) !== (task?.assignee_id ?? null)
            }
        >
            <div className="py-2">
                {error && (
                    <div className="mb-4 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 text-sm">
                        {error}
                    </div>
                )}

                {user?.id && (
                    <div className="mb-4 flex items-center justify-center">
                        <Button
                            variant="secondary"
                            onClick={handleAssignToMe}
                            disabled={loading || isAssignedToMe}
                            icon={
                                <Icon
                                    name="userCheck"
                                    className="w-4 h-4 text-primary"
                                />
                            }
                        >
                            {t("tasks.modal_assignee.assign_to_me")}
                        </Button>
                    </div>
                )}

                <SearchableMultiSelect
                    label={t("tasks.new_modal.assignees_label")}
                    value={selectedAssignee}
                    onChange={setSelectedAssignee}
                    options={assigneeOptions}
                    placeholder={t("tasks.new_modal.assignees_placeholder")}
                    singleSelect={true}
                    hideFullscreen={true}
                />
            </div>
        </Modal>
    );
};
